// ============================================================================
// DashboardScreen — Phase 2 (Booking Overview live; rest still skeleton)
// ============================================================================

import React, { useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import ReportFilterBar from 'components/common/ReportFilterBar';
import { useDateRangePreset } from 'hooks/useDateRangePreset';
import BookingOverviewWidget from './widgets/BookingOverviewWidget';
import type { DashboardFilter } from 'api/types';
import './dashboard.css';

interface ComingSoonProps {
  phaseLabel: string;
  description: string;
}
const ComingSoon: React.FC<ComingSoonProps> = ({ phaseLabel, description }) => (
  <div className="coming-soon-banner">
    <div className="coming-soon-banner-title">{phaseLabel}</div>
    <div className="coming-soon-banner-meta">{description}</div>
  </div>
);

const DashboardScreen: React.FC = () => {
  const [propertyId, setPropertyId] = React.useState<string>('');
  const dateRange = useDateRangePreset('MTD');

  // Stable filter object → drives every widget hook. New object only when one
  // of the inputs actually changes, otherwise the widget hooks would refetch
  // on every render.
  const filter = useMemo<DashboardFilter>(
    () => ({ propertyId, from: dateRange.value.from, to: dateRange.value.to }),
    [propertyId, dateRange.value.from, dateRange.value.to],
  );
  const enabled = !!propertyId;

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Dashboard"
        description="Property KPIs, revenue, approvals, occupancy and partner performance — all in one place."
        icon={<IconSystem.dashboard size={18} />}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ flex: 1, overflow: 'auto', padding: '0 1rem 1rem' }}>

          <ReportFilterBar
            propertyId={propertyId}
            onPropertyChange={setPropertyId}
            preset={dateRange.value.preset}
            from={dateRange.value.from}
            to={dateRange.value.to}
            onPresetChange={dateRange.setPreset}
            onFromChange={dateRange.setCustomFrom}
            onToChange={dateRange.setCustomTo}
          />

          {/* ── Section 1 — Booking Overview ───────────────────────────── */}
          <div className="dashboard-section">
            <h3 className="dashboard-section-title">
              <IconSystem.file size={16} /> Booking Overview
            </h3>
            {enabled ? (
              <BookingOverviewWidget filter={filter} enabled={enabled} />
            ) : (
              <ComingSoon
                phaseLabel="Pick a property"
                description="Select a property above to load booking KPIs."
              />
            )}
          </div>

          {/* ── Section 2 — Revenue ────────────────────────────────────── */}
          <div className="dashboard-section">
            <h3 className="dashboard-section-title">
              <IconSystem.dollarSign size={16} /> Revenue
            </h3>
            <ComingSoon
              phaseLabel="Coming in Phase 3"
              description="Gross / Net / Discount / Comp totals; revenue by room category and meal plan."
            />
          </div>

          {/* ── Section 3 — Approvals ──────────────────────────────────── */}
          <div className="dashboard-section">
            <h3 className="dashboard-section-title">
              <IconSystem.checkCircle size={16} /> Approvals
            </h3>
            <ComingSoon
              phaseLabel="Coming in Phase 4"
              description="Pending discount, complimentary, advance-override and overdue-advance counts."
            />
          </div>

          {/* ── Section 4 — Forward Occupancy ──────────────────────────── */}
          <div className="dashboard-section">
            <h3 className="dashboard-section-title">
              <IconSystem.calendar size={16} /> Forward Occupancy
            </h3>
            <ComingSoon
              phaseLabel="Coming in Phase 5"
              description="Next 7 / 30-day occupancy with category-wise vacancy heatmap."
            />
          </div>

          {/* ── Section 5 — Agent / Corporate ─────────────────────────── */}
          <div className="dashboard-section">
            <h3 className="dashboard-section-title">
              <IconSystem.user size={16} /> Agent / Corporate
            </h3>
            <ComingSoon
              phaseLabel="Coming in Phase 5"
              description="Top performing agents, corporates and rate plans by booking and revenue."
            />
          </div>

        </div>
      </div>
    </div>
  );
};

export default DashboardScreen;
